import React, { useEffect, useState } from 'react';
import { Space, Typography, Row, Col, Table, Button, message, Modal } from 'antd';
import moment from 'moment';
import { Storage } from 'aws-amplify';
import { useCampaignOrderQueueContext } from '../../../../core/components/campaigns/campaignordersqueue/useCampaignOrderQueueContext';
import CampaignOrderQueueReviewMediaNotesDialog from './CampaignOrderQueueReviewMediaNotesDialog';
import { useCampaignRequestorContext } from '../../../../core/components/campaignRequestor/useCampaignRequestorContext';
import { DownloadOutlined } from '@ant-design/icons';
import { multiImageZipV2 } from '../../../../core/utils/dataloading/multiImageZip';

const { Title, Text } = Typography;

// Single Video Ad Group Component
const VideoAdGroupSection = ({
  adGroupCreative,
  adGroupCreativeIndex,
  campaignIndex,
  lineItemIndex,
  isReviewMode,
  onUpdateMedia,
  onUpdateManyMedias,
  onMediaStatusUpdate,
  loadingUpdateMedia,
  loadingCampaignOrder,
}) => {
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [mediaNotesDialogVisible, setMediaNotesDialogVisible] = useState(false);
  const [processedMedias, setProcessedMedias] = useState([]);
  const [localMedias, setLocalMedias] = useState(adGroupCreative.medias);
  const [videoModalVisible, setVideoModalVisible] = useState(false);
  const [modalVideos, setModalVideos] = useState([]);
  const [selectedVideoIndex, setSelectedVideoIndex] = useState(0);

  // Sync local state with prop changes
  useEffect(() => {
    setLocalMedias(adGroupCreative.medias);
  }, [adGroupCreative.medias]);

  const onSelectionChange = (selectedKeys) => {
    setSelectedRowKeys(selectedKeys);
  };

  const handleResetStatus = async (id) => {
    try {
      message.loading('Updating media status...');

      // Optimistically update local state
      const updatedMedias = localMedias.map(media =>
        media.id === id
          ? { ...media, status: 'PENDING' }
          : media
      );
      setLocalMedias(updatedMedias);

      // Notify parent component to update the summary
      if (onMediaStatusUpdate) {
        onMediaStatusUpdate(campaignIndex, lineItemIndex, adGroupCreativeIndex, [
          { id, status: 'PENDING' }
        ]);
      }

      await onUpdateMedia({
        variables: {
          data: {
            status: 'PENDING',
          },
          where: {
            id,
          },
        },
        context: {
          skipRefetch: true,
        },
      });

      message.destroy();
      message.success('Media status updated successfully');
    } catch (error) {
      message.destroy();
      message.error('Error updating media status');
      // Revert on error
      setLocalMedias(adGroupCreative.medias);
      // Revert parent state too
      if (onMediaStatusUpdate) {
        onMediaStatusUpdate(campaignIndex, lineItemIndex, adGroupCreativeIndex,
          adGroupCreative.medias.map(m => ({ id: m.id, status: m.status }))
        );
      }
    }
  };

  const handleResetNotes = async (id) => {
    try {
      message.loading('Updating media notes...');

      // Optimistically update local state
      const updatedMedias = localMedias.map(media =>
        media.id === id
          ? { ...media, mediaNotes: '', status: 'PENDING' }
          : media
      );
      setLocalMedias(updatedMedias);

      // Notify parent component to update the summary
      if (onMediaStatusUpdate) {
        onMediaStatusUpdate(campaignIndex, lineItemIndex, adGroupCreativeIndex, [
          { id, mediaNotes: '', status: 'PENDING' }
        ]);
      }

      await onUpdateMedia({
        variables: {
          data: {
            mediaNotes: '',
            status: 'PENDING',
          },
          where: {
            id,
          },
        },
        context: {
          skipRefetch: true,
        },
      });

      message.destroy();
      message.success('Media notes updated successfully');
    } catch (error) {
      message.destroy();
      message.error('Error updating media notes');
      // Revert on error
      setLocalMedias(adGroupCreative.medias);
      // Revert parent state too
      if (onMediaStatusUpdate) {
        onMediaStatusUpdate(campaignIndex, lineItemIndex, adGroupCreativeIndex,
          adGroupCreative.medias.map(m => ({ id: m.id, mediaNotes: m.mediaNotes, status: m.status }))
        );
      }
    }
  };

  const handleApproveSelections = async () => {
    try {
      message.loading('Updating media status...');

      // Optimistically update local state
      const updatedMedias = localMedias.map(media =>
        selectedRowKeys.includes(media.id)
          ? { ...media, status: 'APPROVED' }
          : media
      );
      setLocalMedias(updatedMedias);

      // Notify parent component to update the summary
      if (onMediaStatusUpdate) {
        const updates = selectedRowKeys.map(id => ({ id, status: 'APPROVED' }));
        onMediaStatusUpdate(campaignIndex, lineItemIndex, adGroupCreativeIndex, updates);
      }

      await onUpdateManyMedias({
        variables: {
          data: {
            status: 'APPROVED',
          },
          where: {
            id_in: selectedRowKeys,
          },
        },
        context: {
          skipRefetch: true,
        },
      });

      setSelectedRowKeys([]);

      message.destroy();
      message.success('Media status updated successfully');
    } catch (error) {
      message.destroy();
      message.error('Error updating media');
      // Revert on error
      setLocalMedias(adGroupCreative.medias);
      // Revert parent state too
      if (onMediaStatusUpdate) {
        onMediaStatusUpdate(campaignIndex, lineItemIndex, adGroupCreativeIndex,
          adGroupCreative.medias.map(m => ({ id: m.id, status: m.status }))
        );
      }
    }
  };

  const handleDownloadAllVideos = () => {
    multiImageZipV2(adGroupCreative.name, adGroupCreative.medias);
  };

  // Process medias to get signed URLs for video preview
  useEffect(() => {
    const processMedias = async () => {
      if (!localMedias || localMedias.length === 0) {
        setProcessedMedias([]);
        return;
      }

      const processed = await Promise.all(
        localMedias.map(async (media) => {
          try {
            const previewUrl = media.key
              ? await Storage.get(media.key, { expires: 600000 })
              : media.url;
            return { ...media, previewUrl };
          } catch (e) {
            return { ...media, previewUrl: media.url };
          }
        })
      );
      setProcessedMedias(processed);
    };

    processMedias();
  }, [localMedias]);

  // Enhanced Video Carousel Modal Component
  const VideoCarouselModal = ({ videos, visible, onClose, initialIndex = 0 }) => {
    const [currentVideoIndex, setCurrentVideoIndex] = useState(initialIndex);

    useEffect(() => {
      setCurrentVideoIndex(initialIndex);
    }, [initialIndex]);

    const currentVideo = videos[currentVideoIndex];

    return (
      <Modal
        open={visible}
        title={`Video Player (${currentVideoIndex + 1} of ${videos.length})`}
        onCancel={onClose}
        footer={null}
        width={900}
        style={{ top: 20 }}
      >
        <div style={{ marginBottom: 16 }}>
          {/* Main Video Player */}
          {currentVideo?.previewUrl && (
            <video
              key={currentVideo.previewUrl}
              controls
              width="100%"
              style={{ maxHeight: '50vh', backgroundColor: '#000' }}
              autoPlay={false}
            >
              <source src={currentVideo.previewUrl} type="video/mp4" />
              Your browser does not support the video tag.
            </video>
          )}

          {/* Video Info */}
          <div style={{ padding: '8px 0', borderBottom: '1px solid #f0f0f0' }}>
            <Text strong>{currentVideo?.name}</Text>
            {currentVideo?.fileType && (
              <Text type="secondary" style={{ marginLeft: 8 }}>
                ({currentVideo.fileType})
              </Text>
            )}
          </div>
        </div>

        {/* Thumbnail Strip */}
        {videos.length > 1 && (
          <div>
            <Text strong style={{ marginBottom: 8, display: 'block' }}>
              Select Video ({videos.length} videos):
            </Text>
            <div
              style={{
                display: 'flex',
                gap: 8,
                overflowX: 'auto',
                padding: '8px 0',
                maxHeight: '120px',
              }}
            >
              {videos.map((video, index) => (
                <div
                  key={index}
                  onClick={() => setCurrentVideoIndex(index)}
                  style={{
                    width: 100,
                    height: 75,
                    cursor: 'pointer',
                    border: currentVideoIndex === index ? '2px solid #1890ff' : '1px solid #d9d9d9',
                    borderRadius: 4,
                    overflow: 'hidden',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: '#f5f5f5',
                    position: 'relative',
                    flexShrink: 0,
                  }}
                >
                  {video.previewUrl ? (
                    <>
                      <video
                        src={video.previewUrl}
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        muted
                        preload="metadata"
                      />
                      {/* Play icon overlay */}
                      <div
                        style={{
                          position: 'absolute',
                          top: '50%',
                          left: '50%',
                          transform: 'translate(-50%, -50%)',
                          fontSize: '16px',
                          color: 'white',
                          textShadow: '0 0 4px rgba(0,0,0,0.8)',
                        }}
                      >
                        {currentVideoIndex === index ? '⏸' : '▶'}
                      </div>
                      {/* Video number badge */}
                      <div
                        style={{
                          position: 'absolute',
                          top: 2,
                          right: 2,
                          backgroundColor: 'rgba(0,0,0,0.7)',
                          color: 'white',
                          fontSize: '10px',
                          padding: '1px 4px',
                          borderRadius: 2,
                        }}
                      >
                        {index + 1}
                      </div>
                    </>
                  ) : (
                    <Text type="secondary" style={{ fontSize: 10 }}>
                      No Preview
                    </Text>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </Modal>
    );
  };

  const VideoPreviewCell = ({ videoUrl, onClick }) => (
    <div onClick={onClick} style={{ width: 120, height: 80, cursor: 'pointer' }}>
      <video
        src={videoUrl}
        style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 4 }}
        muted
        preload="metadata"
      />
      {/* Play icon overlay */}
      <div
        style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          fontSize: '20px',
          color: 'white',
          textShadow: '0 0 4px rgba(0,0,0,0.8)',
          pointerEvents: 'none',
        }}
      >
        ▶
      </div>
    </div>
  );

  const columns = [
    {
      title: 'Video Preview',
      dataIndex: 'previewUrl',
      key: 'previewUrl',
      width: 160,
      render: (previewUrl, _, index) =>
        previewUrl ? (
          <div style={{ position: 'relative' }}>
            <VideoPreviewCell
              videoUrl={previewUrl}
              onClick={() => {
                setModalVideos(processedMedias);
                setSelectedVideoIndex(index);
                setVideoModalVisible(true);
              }}
            />
          </div>
        ) : null,
    },
    {
      title: 'Video Name',
      dataIndex: 'name',
      key: 'name',
      ellipsis: true,
      width: 200,
    },
    {
      title: 'Dimensions',
      key: 'dimensions',
      width: 120,
      render: (_, record) => `${record.width} x ${record.height}`,
    },
    {
      title: 'File Type',
      dataIndex: 'fileType',
      key: 'fileType',
      width: 100,
    },
    {
      title: 'Notes',
      dataIndex: 'mediaNotes',
      key: 'mediaNotes',
      width: 150,
      render: (notes, record) => {
        if (notes) {
          return (
            <Space direction="vertical" size="small">
              <Text ellipsis style={{ maxWidth: 100 }}>
                {notes}
              </Text>
              <Button
                type="text"
                size="small"
                disabled={loadingUpdateMedia || loadingCampaignOrder}
                onClick={() => handleResetNotes(record.id)}
              >
                <Text
                  strong
                  style={{
                    color: '#1890FF',
                    cursor: 'pointer',
                    fontSize: '12px',
                  }}
                >
                  Reset
                </Text>
              </Button>
            </Space>
          );
        }
      },
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (status, record) => {
        switch (status) {
          case 'APPROVED':
            return (
              <Space direction="vertical" size="small">
                <Text type="success" strong>
                  Approved
                </Text>

                {isReviewMode && (
                  <Button
                    type="text"
                    size="small"
                    disabled={loadingUpdateMedia || loadingCampaignOrder}
                    onClick={() => handleResetStatus(record.id)}
                  >
                    <Text
                      strong
                      style={{
                        color: '#1890FF',
                        cursor: 'pointer',
                        fontSize: '12px',
                      }}
                    >
                      Reset
                    </Text>
                  </Button>
                )}
              </Space>
            );

          default:
            return '';
        }
      },
    },
  ];

  return (
    <div
      id={`campaign-${campaignIndex}-lineitem-${lineItemIndex}-creative-${adGroupCreativeIndex}`}
      className="requester-table-wrapper"
      style={{ marginBottom: 24 }}
    >
      <div className="requester-table-header">
        <Row>
          <Col span={12}>
            <Title level={5}>
              VIDEO AD GROUP {adGroupCreativeIndex + 1} - {adGroupCreative.name}
            </Title>
          </Col>
        </Row>
      </div>
      <div style={{ padding: 20 }}>
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <Row gutter={20}>
            <Col span={10}>
              <Space direction="vertical" size="small" style={{ width: '100%' }}>
                <Text type="secondary">Click-Through Link</Text>
                <Text>{adGroupCreative.clickthrough || 'Not provided'}</Text>
              </Space>
            </Col>
            <Col span={4}>
              <Space direction="vertical" size="small" style={{ width: '100%' }}>
                <Text type="secondary">Video Assets</Text>
                <Text>{adGroupCreative.medias.length}</Text>
              </Space>
            </Col>
            <Col span={4}>
              <Space direction="vertical" size="small" style={{ width: '100%' }}>
                <Text type="secondary">Start Date</Text>
                <Text>{moment(adGroupCreative.startDate).format('MM/DD/YYYY')}</Text>
              </Space>
            </Col>
            <Col span={4}>
              <Space direction="vertical" size="small" style={{ width: '100%' }}>
                <Text type="secondary">End Date</Text>
                <Text>{moment(adGroupCreative.endDate).format('MM/DD/YYYY')}</Text>
              </Space>
            </Col>
          </Row>

          <div className="requester-table-wrapper" style={{ marginTop: 20 }}>
            <div className="requester-table-header">
              <Row>
                <Col span={12}>
                  <Title level={5}>VIDEO ASSETS</Title>
                </Col>
                <Col span={12}>
                  <div
                    style={{
                      display: 'flex',
                      gap: 8,
                      justifyContent: 'flex-end',
                    }}
                  >
                    {isReviewMode && (
                      <div
                        style={{
                          display: 'flex',
                          gap: 8,
                          justifyContent: 'flex-end',
                        }}
                      >
                        <Button
                          type="default"
                          onClick={handleApproveSelections}
                          disabled={!selectedRowKeys.length}
                        >
                          Approve Selection(s)
                        </Button>
                        <Button
                          type="default"
                          onClick={() => setMediaNotesDialogVisible(true)}
                          disabled={!selectedRowKeys.length}
                        >
                          Send Notes
                        </Button>
                      </div>
                    )}

                    <Button
                      type="default"
                      disabled={!adGroupCreative.medias.length}
                      onClick={handleDownloadAllVideos}
                    >
                      <DownloadOutlined />
                      Download All Videos
                    </Button>
                  </div>
                </Col>
              </Row>
            </div>

            <Table
              rowKey="id"
              rowSelection={
                isReviewMode
                  ? {
                      selectedRowKeys: selectedRowKeys,
                      onChange: onSelectionChange,
                    }
                  : null
              }
              columns={columns}
              dataSource={processedMedias}
              pagination={false}
              className="requester-table"
              scroll={{ x: 800 }}
            />
          </div>
        </Space>
      </div>

      {/* VIDEO CAROUSEL MODAL */}
      <VideoCarouselModal
        videos={modalVideos}
        visible={videoModalVisible}
        onClose={() => {
          setVideoModalVisible(false);
          setModalVideos([]);
          setSelectedVideoIndex(0);
        }}
        initialIndex={selectedVideoIndex}
      />

      {/* MEDIA NOTES DIALOG */}
      {mediaNotesDialogVisible && (
        <CampaignOrderQueueReviewMediaNotesDialog
          mediaIds={selectedRowKeys}
          visible={mediaNotesDialogVisible}
          setVisible={setMediaNotesDialogVisible}
          medias={adGroupCreative.medias}
        />
      )}
    </div>
  );
};

// Main Component
export const CampaignOrderQueueReviewVideoAssets = ({
  adGroup,
  campaignIndex,
  lineItemIndex,
  onMediaStatusUpdate,
}) => {
  const [isReviewMode, setIsReviewMode] = useState(false);

  const { updateMedia, updateManyMedias, loadingUpdateMedia } = useCampaignOrderQueueContext();
  const { currentCampaignOrder, loadingCampaignOrder } = useCampaignRequestorContext();

  useEffect(() => {
    // Check if current campaign order status enables review mode
    if (
      currentCampaignOrder.status === 'PENDING' ||
      currentCampaignOrder.status === 'REJECTED' ||
      currentCampaignOrder.status === 'LIVE_PENDING' ||
      currentCampaignOrder.status === 'PAUSED_PENDING'
    ) {
      setIsReviewMode(true);
    }
  }, [currentCampaignOrder]);

  if (!adGroup || !adGroup.adGroupCreatives || adGroup.adGroupCreatives.length === 0) {
    return null;
  }

  return (
    <div>
      {adGroup.adGroupCreatives.map((adGroupCreative, index) => (
        <VideoAdGroupSection
          key={index}
          adGroupCreative={adGroupCreative}
          adGroupCreativeIndex={index}
          campaignIndex={campaignIndex}
          lineItemIndex={lineItemIndex}
          isReviewMode={isReviewMode}
          onUpdateMedia={updateMedia}
          onUpdateManyMedias={updateManyMedias}
          onMediaStatusUpdate={onMediaStatusUpdate}
          loadingUpdateMedia={loadingUpdateMedia}
          loadingCampaignOrder={loadingCampaignOrder}
        />
      ))}
    </div>
  );
};
